import type {ReactElement} from 'react';
import React from 'react';
import type {AnimationLightboxHeaderContentProps} from './animation-lightbox-header-content.types';
import styles from './animation-lightbox-header-content.module.scss';
import {Icon} from '@Components/icon-v2';
import {IconShape, IconSize, IconType} from '@Components/icon-v2/icon.types';
import {Button, Size, Type} from '@Components/button';
import {getUrlForAiChatAnimation} from '@Libraries/ai-chat.library';
import {downloadTrimmedAiChatAnimation} from '@Components/ai-chat-window/lib/chat-attachment-image.library';
import {downloadBlobAsFile} from '@Utils/file.util';
import {useOpenChatAnimationInEditor} from '@Components/ai-chat-window/hooks/useOpenChatAnimationInEditor';
import {Dropdown} from '@Components/dropdown-v2';
import {ALIGNMENT_TYPE} from '@Components/dropdown-v2/dropdown.types';
import {PublishingOptions} from '@Components/ai-chat-window/components/publishing-options/publishing-options';
import {LoadingStates} from '@Utils/loading.util';
import {useAiChat} from '@AiChat/providers/ai-chat.context';
import {usePublishChatAnimation} from '@Components/ai-chat-window/hooks/usePublishChatAnimation';
import {useResetAnimationActionLoadingStatesOnModalClose} from '@Components/ai-chat-window/hooks/useResetAnimationActionLoadingStatesOnModalClose';
import {GA4EventName} from '@Libraries/ga-events';

export function AnimationLightboxHeaderContent({onClose, chatId, animationHashedId, isWriteActionEnabled, isCreating, startTime, endTime, duration, isMuted}: AnimationLightboxHeaderContentProps): ReactElement {
  const {state, actions} = useAiChat();
  const {openAnimationInEditor, isLoading} = useOpenChatAnimationInEditor({chatId, animationHashedId});
  const {publishToSocialMedia, promoteAsEvent, publishAsEmailCampaign} = usePublishChatAnimation(chatId, animationHashedId);

  useResetAnimationActionLoadingStatesOnModalClose(chatId, animationHashedId);

  const loadingStates = state.getLoadingStatesForAnimationActions(chatId, animationHashedId);
  const isPublishingLoading = loadingStates.publishingLoadingState === LoadingStates.LOADING;
  const isDownloadLoading = loadingStates.downloadLoadingState === LoadingStates.LOADING;
  const isDisabled = isLoading || isPublishingLoading || isDownloadLoading || isCreating;

  const isTrimmed = startTime !== undefined && endTime !== undefined && duration !== undefined && (startTime > 0 || endTime < duration);

  const handleDownload = async (): Promise<void> => {
    window.PMW.gtm.trackGA4CustomEvent(GA4EventName.AICHAT_TURNTOVIDEO_DOWNLOAD);
    actions.updateLoadingStatesForAnimationActions(chatId, animationHashedId, {
      downloadLoadingState: LoadingStates.LOADING,
    });
    try {
      if (isTrimmed || isMuted) {
        await downloadTrimmedAiChatAnimation(animationHashedId, isMuted);
      } else {
        const res = await fetch(getUrlForAiChatAnimation(animationHashedId));
        const blob = await res.blob();
        downloadBlobAsFile(blob, `PosterMyWall_GeneratedVideo_${animationHashedId.substring(0, 8)}.mp4`);
      }
    } finally {
      actions.updateLoadingStatesForAnimationActions(chatId, animationHashedId, {
        downloadLoadingState: LoadingStates.NOT_LOADED,
      });
    }
  };

  return (
    <div className={styles.lightboxHeader}>
      <Icon
        icon={'icon-arrow-left'}
        size={IconSize.SIZE_ICON_16}
        type={IconType.DARK_GHOST}
        ariaLabel={window.i18next.t('pmwjs_back')}
        shape={IconShape.SQUARE}
        onClick={onClose}
      />
      <div className={styles.headerActions}>
        <Button
          text={window.i18next.t('pmwjs_edit_video_in_editor')}
          size={Size.XSMALL}
          type={Type.DARK_GHOST}
          icon={'icon-pencil'}
          isLoading={isLoading}
          disabled={!isWriteActionEnabled || isDisabled}
          onClick={(): void => {
            void openAnimationInEditor();
          }}
        />
        {state.isSmall ? (
          <Icon
            icon={'icon-download'}
            size={IconSize.SIZE_ICON_16}
            type={IconType.DARK_SECONDARY}
            shape={IconShape.SQUARE}
            onClick={(): void => {
              void handleDownload();
            }}
            isDisabled={isDisabled}
          />
        ) : (
          <Button
            text={window.i18next.t('pmwjs_download')}
            size={Size.XSMALL}
            type={Type.DARK_SECONDARY}
            icon={'icon-download'}
            isLoading={isDownloadLoading}
            disabled={isDisabled}
            onClick={(): void => {
              void handleDownload();
            }}
          />
        )}
        <Dropdown
          showModalForSmallScreen
          modalTitle={window.i18next.t('pmwjs_publish')}
          popupClassName={styles.actionPopup}
          className={styles.positionUnset}
          selector={
            <Button
              text={window.i18next.t('pmwjs_publish')}
              size={Size.XSMALL}
              type={Type.SECONDARY}
              icon={'icon-publish-arrow'}
              isLoading={isPublishingLoading}
              disabled={isDisabled || !isWriteActionEnabled}
            />
          }
          popup={
            <PublishingOptions
              chatId={chatId}
              hashedId={animationHashedId}
              loadingStates={loadingStates}
              publishToSocialMedia={publishToSocialMedia}
              publishAsEmailCampaign={publishAsEmailCampaign}
              promoteAsEvent={promoteAsEvent}
            />
          }
          alignment={ALIGNMENT_TYPE.BOTTOM_END}
        />
      </div>
    </div>
  );
}
